/** Public Home — Porto Sant'Elpidio landing. */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, Store, ArrowRight, Radio } from 'lucide-react';
import { fetchHome } from '@/services/api/public';
import { getPublicConfig } from '@/services/api/config';
import { mediaUrl } from '@/services/api/media';
import { Container, ItemCard, PageTitle, fmtDateTime } from '@/pages/public/_shared';
import { PUBLIC_IDS } from '@/constants/testIds';

const HERO_FALLBACK = 'https://images.unsplash.com/photo-1632568373601-f68a5faf16fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxJdGFsaWFuJTIwc2Vhc2lkZSUyMHByb21lbmFkZSUyMGx1bmdvbWFyZSUyMHN1bnNldHxlbnwwfHx8fDE3ODQxMjc5OTV8MA&ixlib=rb-4.1.0&q=85&w=1920';

function EventGrid({ items, badge }) {
        return (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {items.map((e, i) => (
                                <ItemCard key={e.id} index={i} to={`/eventi/${e.slug}`} title={e.title}
                                        tag={badge || e.category} cover={e.cover_url} coverIcon={CalendarDays} meta={fmtDateTime(e.start_at)} titleLines={2} />
                        ))}
                </div>
        );
}

export default function PublicHome() {
        const { data } = useQuery({ queryKey: ['public-home'], queryFn: fetchHome });
        const { data: cfg } = useQuery({ queryKey: ['public-config'], queryFn: getPublicConfig });
        const muni = data?.municipality;
        const counts = data?.counts || {};
        const ongoing = data?.ongoing_events || [];
        const upcoming = data?.upcoming_events || [];
        const heroImg = cfg?.hero_url ? mediaUrl(cfg.hero_url) : HERO_FALLBACK;
        const subtitle = cfg?.territory_description || cfg?.short_description
                || 'Scopri eventi, luoghi e attività del territorio in tempo reale.';

        return (
                <div data-testid={PUBLIC_IDS.home}>
                        <section className="relative overflow-hidden border-b border-slate-800">
                                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} aria-hidden="true" />
                                <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/70 to-[#0b1120]" aria-hidden="true" />
                                <Container className="relative py-16 sm:py-24">
                                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs font-semibold uppercase tracking-[0.35em] text-emerald-300 sm:text-sm">
                                                {muni ? `${muni.province} · ${muni.region}` : 'Comune'}
                                        </motion.p>
                                        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                                                className="mt-3 text-5xl font-extrabold leading-[1.05] tracking-tight text-white drop-shadow-xl sm:text-6xl lg:text-7xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                                {muni?.name || 'Porto Sant\u2019Elpidio'}
                                        </motion.h1>
                                        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                                                className="mt-5 max-w-xl text-lg font-medium text-slate-100 drop-shadow-md sm:text-xl">
                                                {subtitle}
                                        </motion.p>
                                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-8 flex flex-wrap items-center gap-3">
                                                <Link to="/eventi" data-testid="hero-cta-events"
                                                        className="group inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-300">
                                                        Esplora gli eventi
                                                        <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                                                </Link>
                                                <div className="flex flex-wrap gap-2">
                                                        {[['/luoghi', 'Luoghi', counts.places, MapPin], ['/attivita', 'Attività', counts.activities, Store]].map(([to, label, n, Icon]) => (
                                                                <Link key={to} to={to} className="flex items-center gap-2 rounded-full border border-slate-600 bg-slate-950/50 px-4 py-3 text-sm backdrop-blur-sm transition-colors hover:border-emerald-400/50">
                                                                        <Icon size={15} className="text-emerald-300" /> {label} <span className="text-slate-400">{n ?? 0}</span>
                                                                </Link>
                                                        ))}
                                                </div>
                                        </motion.div>
                                </Container>
                        </section>

                        {ongoing.length > 0 && (
                                <Container className="pb-0">
                                        <div className="mb-6 flex items-center gap-2">
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-400/10 px-3 py-1 text-xs font-semibold text-rose-300">
                                                        <Radio size={13} className="animate-pulse" /> In corso ora
                                                </span>
                                        </div>
                                        <EventGrid items={ongoing} badge="In corso" />
                                </Container>
                        )}

                        <Container>
                                <div className="flex items-end justify-between">
                                        <PageTitle sub="In programma prossimamente">Prossimi eventi</PageTitle>
                                        <Link to="/eventi" className="mb-8 flex items-center gap-1 text-sm text-emerald-300">Tutti <ArrowRight size={14} /></Link>
                                </div>
                                {upcoming.length > 0
                                        ? <EventGrid items={upcoming} />
                                        : <p className="text-slate-500">Nessun evento in programma.</p>}
                        </Container>
                </div>
        );
}
